import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaw,
  faArrowRight,
  faArrowLeft,
  faHome,
  faClock,
  faHeart,
  faCheckCircle,
  faRotateLeft,
  faPlay
} from '@fortawesome/free-solid-svg-icons';
import Button from '../components/Button';
import BreedRecommendations from '../components/BreedRecommendations';
import { useQuizState } from '../hooks/useQuizState';
import { getQuizTimestamp } from '../utils/quizStorage';

function Quiz() {
  const questions = [
    {
      id: 'experience',
      title: 'What is your experience with pets?',
      options: [
        { value: 'first-time', label: 'First-time pet owner', icon: faHeart },
        { value: 'some', label: 'Some experience with pets', icon: faPaw },
        { value: 'experienced', label: 'Very experienced with pets', icon: faCheckCircle },
      ],
    },
    {
      id: 'living',
      title: 'What is your living situation?',
      options: [
        { value: 'apartment', label: 'Apartment/Condo', icon: faHome },
        { value: 'house-small', label: 'House with small yard', icon: faHome },
        { value: 'house-large', label: 'House with large yard', icon: faHome },
        { value: 'farm', label: 'Farm/Rural property', icon: faHome },
      ],
    },
    {
      id: 'time',
      title: 'How much time can you dedicate to your pet daily?',
      options: [
        { value: 'minimal', label: '1-2 hours', icon: faClock },
        { value: 'moderate', label: '3-4 hours', icon: faClock },
        { value: 'lots', label: '5+ hours', icon: faClock },
        { value: 'all-day', label: 'Most of the day', icon: faClock },
      ],
    },
    {
      id: 'size',
      title: 'What size pet do you prefer?',
      options: [
        { value: 'small', label: 'Small (under 25 lbs)', icon: faPaw },
        { value: 'medium', label: 'Medium (25-60 lbs)', icon: faPaw },
        { value: 'large', label: 'Large (60+ lbs)', icon: faPaw },
        { value: 'any', label: 'No preference', icon: faHeart },
      ],
    },
    {
      id: 'energy',
      title: 'What energy level are you looking for?',
      options: [
        { value: 'low', label: 'Low energy - likes to relax', icon: faHeart },
        { value: 'moderate', label: 'Moderate - some playtime', icon: faPaw },
        { value: 'high', label: 'High energy - loves to play', icon: faCheckCircle },
        { value: 'very-high', label: 'Very high - needs lots of exercise', icon: faCheckCircle },
      ],
    },
  ];

  const {
    currentQuestion,
    answers,
    isComplete,
    canResume,
    totalAnswered,
    progress,
    handleAnswer,
    nextQuestion,
    prevQuestion,
    restartQuiz,
    resumeFromLastQuestion,
  } = useQuizState(questions);

  // Format timestamp for display
  const formatLastCompleted = () => {
    const timestamp = getQuizTimestamp();
    if (!timestamp) return null;
    
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show resume option if user has partial progress
  if (canResume && !isComplete) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
            <FontAwesomeIcon icon={faPaw} className="h-16 w-16 text-primary-500 mb-6" />
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">
              Welcome Back!
            </h1>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 mb-6">
              We found your previous quiz progress. You&apos;ve answered {totalAnswered} of {questions.length} questions.
            </p>
            
            <div className="bg-amber-50 dark:bg-amber-900 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-amber-900 dark:text-amber-100 mb-2">
                Continue Where You Left Off
              </h2>
              <p className="text-amber-700 dark:text-amber-200">
                Your answers are automatically saved as you progress through the quiz.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="primary" onClick={resumeFromLastQuestion}>
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Continue Quiz
              </Button>
              <Button variant="outline" onClick={restartQuiz}>
                <FontAwesomeIcon icon={faRotateLeft} className="mr-2" />
                Start Over
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isComplete) {
    const lastCompleted = formatLastCompleted();
    
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
            <FontAwesomeIcon icon={faCheckCircle} className="h-16 w-16 text-green-500 mb-6" />
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">
              Quiz Complete!
            </h1>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 mb-4">
              Thank you for completing our pet matching quiz. We&apos;re analyzing your answers to find your perfect matches.
            </p>
            
            {lastCompleted && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8">
                Completed on {lastCompleted}
              </p>
            )}
            
            <div className="bg-primary-50 dark:bg-primary-900 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-primary-900 dark:text-primary-100 mb-4">
                Your Quiz Responses
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {questions.map((question) => {
                  const answer = answers[question.id];
                  const selectedOption = question.options.find(opt => opt.value === answer);
                  return (
                    <div key={question.id} className="bg-white dark:bg-neutral-800 rounded-lg p-4">
                      <h3 className="font-medium text-primary-800 dark:text-primary-200 mb-2 text-sm">
                        {question.title}
                      </h3>
                      <p className="text-primary-700 dark:text-primary-300">
                        {selectedOption?.label || 'No answer'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Breed Recommendations */}
            <div className="mb-8">
              <BreedRecommendations userPreferences={answers} limit={3} />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="primary" onClick={restartQuiz}>
                <FontAwesomeIcon icon={faRotateLeft} className="mr-2" />
                Take Quiz Again
              </Button>
              <Button variant="outline">
                Browse All Pets
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const currentAnswer = answers[currentQ.id];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-neutral-100 dark:bg-neutral-700 h-2">
            <div 
              className="bg-primary-500 h-2 transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faPaw} className="h-8 w-8 text-primary-500 mr-3" />
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                  Pet Compatibility Quiz
                </h1>
              </div>
              <p className="text-neutral-600 dark:text-neutral-300">
                Question {currentQuestion + 1} of {questions.length}
              </p>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 text-center">
                {currentQ.title}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentQ.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQ.id, option.value)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                      currentAnswer === option.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-200'
                        : 'border-neutral-200 dark:border-neutral-600 hover:border-primary-300 dark:hover:border-primary-400 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon 
                        icon={option.icon} 
                        className={`h-5 w-5 ${
                          currentAnswer === option.value 
                            ? 'text-primary-500' 
                            : 'text-neutral-400 dark:text-neutral-500'
                        }`}
                      />
                      <span className="font-medium">{option.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={prevQuestion}
                disabled={currentQuestion === 0}
                className={currentQuestion === 0 ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Previous
              </Button>

              <Button
                variant="primary"
                onClick={nextQuestion}
                disabled={!currentAnswer}
                className={!currentAnswer ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {currentQuestion === questions.length - 1 ? 'Complete Quiz' : 'Next'}
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Quiz;